/* eslint-disable */
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { connect } from 'react-redux'
import {
  Button,
  Card,
  message,
  Popconfirm,
  Popover,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import { PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import PageHeader from '@/components/PageHeader'
import Permission from '@/components/PermissionA5'
import { deleteSystemRole } from '@/service/systemControl'
import RoleAssignPermissionDrawer from '@/views/systemControl/roleList/share/RoleAssignPermissionDrawer'
import RoleAddMemberModal from '@/views/systemControl/roleList/share/RoleAddMemberModal'
import RoleCreateDrawer from '@/views/systemControl/roleList/share/RoleCreateDrawer'
import { fetchEnumOptions } from '@/redux/components/enumOptionsSlice'
import { fetchSystemPermissionsList } from '@/redux/use/systemPermissionSlice'
import { fetchJStoreList } from '@/redux/components/getJStoreListSlice'
import { fetchSystemRolesList } from '@/redux/use/systemRoleSlice'
import {
  enrichRolePermissionsWithAncestors,
  groupPermissionNodesThreeLevels,
  pickRolePermissionNodes
} from '@/utils/rolePermissionTree'
import { formatDateTimeStr, renderTextStr } from '@/utils/renderUtils'

const { Text, Paragraph } = Typography

/** 类组件内无法用 useToken，与默认主题 token 对齐的展示用间距与色板 */
const PAGE_TOKEN = {
  marginXXS: 4,
  marginSM: 8,
  borderRadiusSM: 6,
  colorFillQuaternary: 'rgba(0, 0, 0, 0.04)',
  colorPrimary: '#1677ff'
}

const breadcrumbList = [
  { breadcrumbName: '首页', href: '/client/commandCenter' },
  { breadcrumbName: '用户中心' },
  { breadcrumbName: '角色管理' }
]

/** 角色用户列：单元格内最多展示约 2 行 Tag（与 antd 默认 Tag 行高约一致） */
const ROLE_USERS_TAG_LINE_HEIGHT_PX = 24
const ROLE_USERS_MAX_LINES = 2
const ROLE_USERS_CELL_MAX_HEIGHT_PX =
  ROLE_USERS_TAG_LINE_HEIGHT_PX * ROLE_USERS_MAX_LINES + PAGE_TOKEN.marginXXS * (ROLE_USERS_MAX_LINES - 1)

function buildStoreIdToNameMap(storeOptions) {
  const map = new Map()
  if (!Array.isArray(storeOptions)) return map
  for (const o of storeOptions) {
    if (o && o.id != null) {
      map.set(String(o.id), o.value || '')
    }
  }
  return map
}

function buildOptionValueToLabelMap(options) {
  const map = new Map()
  if (!Array.isArray(options)) return map
  for (const o of options) {
    if (o && o.id != null) {
      map.set(String(o.id), o.value || '')
    }
  }
  return map
}

function storeDisplayName(storeId, storeNameById) {
  if (storeId == null || storeId === '') {
    return '未指定门店'
  }
  const k = String(storeId)
  const name = storeNameById.get(k)
  if (name) return name
  return `门店#${k}`
}

/** 同一员工可在多店多条；key 仅含 id+门店，排序变化时 React 可稳定复用节点 */
function roleUserRowKey(u) {
  const idPart = u.id != null ? String(u.id) : `noid`
  const storePart = u.storeId != null && u.storeId !== '' ? String(u.storeId) : 'nostore'
  return `${idPart}-${storePart}`
}

function groupUsersByStore(users, storeNameById, currentUserStoreId) {
  const m = new Map()
  for (let i = 0; i < users.length; i++) {
    const u = users[i]
    const sid = u.storeId != null && u.storeId !== '' ? String(u.storeId) : '_none'
    if (!m.has(sid)) {
      const storeTitle = sid === '_none' ? '未指定门店' : storeDisplayName(u.storeId, storeNameById)
      m.set(sid, { storeKey: sid, storeTitle, users: [] })
    }
    m.get(sid).users.push(u)
  }
  const arr = Array.from(m.values())
  for (const g of arr) {
    g.users.sort((a, b) =>
      String(a.userName || '').localeCompare(String(b.userName || ''), 'zh-Hans-CN')
    )
  }
  arr.sort((a, b) => {
    const aCur = isCurrentLoginStoreGroup(a.storeKey, currentUserStoreId)
    const bCur = isCurrentLoginStoreGroup(b.storeKey, currentUserStoreId)
    if (aCur !== bCur) return aCur ? -1 : 1
    return a.storeTitle.localeCompare(b.storeTitle, 'zh-Hans-CN')
  })
  return arr
}

/** 单元格内 Tag 顺序：当前门店在前，再按店名、姓名排序，便于限高区域内先看到本店 */
function sortUsersForRoleCellDisplay(users, currentUserStoreId, storeNameById) {
  if (!Array.isArray(users) || users.length === 0) return []
  const copy = [...users]
  copy.sort((a, b) => {
    const aCur = isUserAtCurrentStore(a, currentUserStoreId)
    const bCur = isUserAtCurrentStore(b, currentUserStoreId)
    if (aCur !== bCur) return aCur ? -1 : 1
    const sa = storeDisplayName(a.storeId, storeNameById)
    const sb = storeDisplayName(b.storeId, storeNameById)
    if (sa !== sb) return sa.localeCompare(sb, 'zh-Hans-CN')
    return String(a.userName || '').localeCompare(String(b.userName || ''), 'zh-Hans-CN')
  })
  return copy
}

/** 与分组 storeKey（String(员工.storeId)）比对；登录态 app.user.storeId 需统一为字符串 */
function isCurrentLoginStoreGroup(storeKey, currentUserStoreId) {
  if (storeKey === '_none') return false
  if (currentUserStoreId == null || currentUserStoreId === '') return false
  return String(storeKey) === String(currentUserStoreId)
}

/** 登录用户所属门店（接口常见 storeId；兼容 store_id） */
function getLoginUserStoreId(user) {
  if (!user || typeof user !== 'object') return ''
  const raw = user.storeId != null && user.storeId !== '' ? user.storeId : user.store_id
  if (raw == null || raw === '') return ''
  return String(raw)
}

/** 表格内单条用户记录是否属当前登录门店 */
function isUserAtCurrentStore(userRecord, currentUserStoreId) {
  if (!currentUserStoreId) return false
  if (userRecord.storeId == null || userRecord.storeId === '') return false
  return String(userRecord.storeId) === String(currentUserStoreId)
}

/** 当前门店用户在列表里的 Tag 样式（单元格内与 Popover 内统一可辨） */
const ROLE_USER_TAG_CURRENT_STORE_STYLE = {
  background: 'rgba(22, 119, 255, 0.12)',
  borderColor: 'rgba(22, 119, 255, 0.45)',
  borderStyle: 'solid',
  borderWidth: 1
}

/**
 * 用户 Tag 区域限制 2 行高度；超出时右侧显示 ··· + Popover 查看全部（Popover 内按门店分组）
 */
function RoleUsersTagsCell({ users, storeNameById, currentUserStoreId }) {
  const containerRef = useRef(null)
  const [clipped, setClipped] = useState(false)

  const displayUsers = useMemo(
    () => sortUsersForRoleCellDisplay(users, currentUserStoreId, storeNameById),
    [users, currentUserStoreId, storeNameById]
  )
  const groups = useMemo(
    () => groupUsersByStore(users, storeNameById, currentUserStoreId),
    [users, storeNameById, currentUserStoreId]
  )

  const measure = useCallback(() => {
    const el = containerRef.current
    if (!el) return
    setClipped(el.scrollHeight > el.clientHeight + 1)
  }, [displayUsers])

  useLayoutEffect(() => {
    measure()
    const el = containerRef.current
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined
    }
    const ro = new ResizeObserver(() => measure())
    ro.observe(el)
    return () => ro.disconnect()
  }, [measure, displayUsers])

  useLayoutEffect(() => {
    const onResize = () => measure()
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [measure])

  const { marginXXS } = PAGE_TOKEN
  const allTagsContent = (
    <div style={{ maxWidth: 400, maxHeight: 400, overflowY: 'auto', paddingRight: 4 }}>
      {groups.map((g) => {
        const isCurrent = isCurrentLoginStoreGroup(g.storeKey, currentUserStoreId)
        return (
        <div
          key={g.storeKey}
          style={{
            marginBottom: 14,
            ...(isCurrent
              ? {
                  padding: '10px 10px 12px',
                  borderRadius: PAGE_TOKEN.borderRadiusSM,
                  background: 'rgba(22, 119, 255, 0.06)'
                }
              : {})
          }}
        >
          <div style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.65)', marginBottom: 6 }}>
            {g.storeTitle}
            <Text type="secondary" style={{ marginLeft: 6, fontSize: 12 }}>
              （{g.users.length}）
            </Text>
            {isCurrent ? (
              <Text style={{ marginLeft: 8, fontSize: 11, color: PAGE_TOKEN.colorPrimary }}>当前门店</Text>
            ) : null}
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: marginXXS,
              alignItems: 'flex-start'
            }}
          >
            {g.users.map((u) => {
              const atCurrent = isUserAtCurrentStore(u, currentUserStoreId)
              return (
              <Tag
                key={roleUserRowKey(u)}
                style={{
                  marginInlineEnd: 0,
                  ...(atCurrent ? ROLE_USER_TAG_CURRENT_STORE_STYLE : {})
                }}
              >
                {renderTextStr(u.userName)}
              </Tag>
              )
            })}
          </div>
        </div>
        )
      })}
    </div>
  )

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: marginXXS,
        width: '100%',
        minWidth: 0
      }}
    >
      <div
        ref={containerRef}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: marginXXS,
          alignItems: 'flex-start',
          alignContent: 'flex-start',
          flex: 1,
          minWidth: 0,
          maxHeight: ROLE_USERS_CELL_MAX_HEIGHT_PX,
          overflow: 'hidden'
        }}
      >
        {displayUsers.map((u) => {
          const storeLabel = storeDisplayName(u.storeId, storeNameById)
          const nameStr = renderTextStr(u.userName)
          const atCurrent = isUserAtCurrentStore(u, currentUserStoreId)
          return (
            <Tag
              key={roleUserRowKey(u)}
              style={{
                marginInlineEnd: 0,
                maxWidth: '100%',
                ...(atCurrent ? ROLE_USER_TAG_CURRENT_STORE_STYLE : {})
              }}
            >
              <span style={{ display: 'inline-flex', alignItems: 'center', maxWidth: '100%' }}>
                <span style={{ flexShrink: 0 }}>{nameStr}</span>
                <Text
                  type="secondary"
                  style={{ fontSize: 11, marginLeft: 4, minWidth: 0 }}
                  ellipsis={{ tooltip: `${nameStr} · ${storeLabel}` }}
                >
                  · {storeLabel}
                </Text>
              </span>
            </Tag>
          )
        })}
      </div>
      {clipped ? (
        <Popover
          title={
            currentUserStoreId
              ? `全部用户（${users.length}）· 按门店 · 本店分组置顶`
              : `全部用户（${users.length}）· 按门店`
          }
          content={allTagsContent}
          trigger="click"
          placement="topLeft"
        >
          <Tag color="default" style={{ marginInlineEnd: 0, cursor: 'pointer', flexShrink: 0 }}>
            ···
          </Tag>
        </Popover>
      ) : null}
    </div>
  )
}

function TreePermissionTooltip({ node }) {
  return (
    <div>
      <div style={{ fontSize: 12 }}>{node.name}</div>
      <div style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4, opacity: 0.85 }}>
        id: {node.id}
        {node.parentId != null && node.parentId !== '' ? (
          <span style={{ marginLeft: 8 }}>parentId: {node.parentId}</span>
        ) : null}
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  fetching: state.use.systemRole.fetching,
  list: state.use.systemRole.list,
  permissionsCatalog: state.use.systemPermission.list,
  jStoreListObj: state.components.getJStoreList,
  enumOptions: state.components.enumOptions,
  user: state.app.user
})

const mapDispatchToProps = {
  fetchSystemRolesList,
  fetchSystemPermissionsList,
  fetchJStoreList,
  fetchEnumOptions
}

class RoleListPage extends React.Component {
  componentDidMount() {
    this.refreshList()
    this.fetchPermissionsPreview()
    this.fetchDataScopeTypeOptions()
    const { fetchJStoreList: loadStores } = this.props
    loadStores({ query: {}, dataType: 'option' })
      .unwrap()
      .catch(() => {})
  }

  fetchPermissionsPreview = () => {
    const { fetchSystemPermissionsList: load } = this.props
    load()
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载权限列表失败'))
  }

  fetchDataScopeTypeOptions = () => {
    const {
      fetchEnumOptions: loadEnum,
      enumOptions: {
        DataScopeType: { fetching = false, data = [] } = {}
      } = {}
    } = this.props
    if (fetching || data.length > 0) {
      return
    }
    loadEnum({ key: 'DataScopeType', dataType: 'option' })
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载资源范围枚举失败'))
  }

  refreshList = () => {
    const { fetchSystemRolesList: load } = this.props
    load({ include: 'permissions,users' })
      .unwrap()
      .catch((err) => message.error(typeof err === 'string' ? err : '加载角色列表失败'))
  }

  handleRoleCreateSuccess = () => {
    this.refreshList()
  }

  handleDeleteRole = async (record) => {
    const id = record?.id
    if (id == null) {
      message.error('缺少角色 id')
      return
    }
    try {
      await deleteSystemRole(id)
      message.success('删除成功')
      this.refreshList()
    } catch (e) {
      message.error(e?.message || e?.msg || '删除失败')
    }
  }

  /** 单个一级权限模块块（紧凑排版，便于 Grid 多列铺满） */
  renderPermissionGroupBlock = (g) => {
    const { borderRadiusSM, colorFillQuaternary, colorPrimary } = PAGE_TOKEN
    return (
      <div
        key={g.key}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 5,
          minWidth: 0,
          width: '100%',
          padding: '4px 6px 4px 5px',
          borderRadius: borderRadiusSM,
          background: colorFillQuaternary,
          borderLeft: `2px solid ${colorPrimary}`
        }}
      >
        <Text
          type="secondary"
          style={{
            fontSize: 12,
            flexShrink: 0,
            fontWeight: 600,
            lineHeight: 1.25
          }}
        >
          {g.root.name}
        </Text>
        {!g.level2.length ? (
          <Tooltip title={<TreePermissionTooltip node={g.root} />}>
            <Tag style={{ marginInlineEnd: 0 }}>
              {g.root.name}
            </Tag>
          </Tooltip>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              width: '100%',
              paddingLeft: 2
            }}
          >
            {g.level2.map((l2) => (
              <div
                key={l2.node.id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 4
                }}
              >
                {l2.level3.length ? (
                  <>
                    <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>
                      {l2.node.name}
                    </Text>
                    {l2.level3.map((l3) => (
                      <Tooltip key={l3.id} title={<TreePermissionTooltip node={l3} />}>
                        <Tag style={{ marginInlineEnd: 0 }}>
                          {l3.name}
                        </Tag>
                      </Tooltip>
                    ))}
                  </>
                ) : (
                  <Tooltip title={<TreePermissionTooltip node={l2.node} />}>
                    <Tag style={{ marginInlineEnd: 0 }}>
                      {l2.node.name}
                    </Tag>
                  </Tooltip>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  /** 权限模块 Grid：多列 auto-fill 吃满列宽，减少纵向堆叠 */
  renderPermissionGroupsGrid = (groups) => {
    const { marginSM } = PAGE_TOKEN
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(132px, 1fr))',
          gap: marginSM,
          width: '100%',
          minWidth: 0,
          alignItems: 'start'
        }}
      >
        {groups.map((g) => this.renderPermissionGroupBlock(g))}
      </div>
    )
  }

  /** 表格内只展示一级模块名摘要（限高），完整树在 Popover，避免行被撑得过高 */
  renderPermissionCell = (row) => {
    const { permissionsCatalog } = this.props
    const picked = pickRolePermissionNodes(row)
    const nodes = enrichRolePermissionsWithAncestors(picked, permissionsCatalog)
    const groups = groupPermissionNodesThreeLevels(nodes)
    if (!groups.length) {
      return <Text type="secondary">—</Text>
    }
    const popoverMaxW =
      typeof window !== 'undefined' ? Math.min(720, window.innerWidth - 48) : 720
    const fullInPopover = (
      <div style={{ maxWidth: popoverMaxW, minWidth: 0 }}>
        {this.renderPermissionGroupsGrid(groups)}
      </div>
    )
    const summary = groups.map((g) => g.root.name).join('、')
    return (
      <div style={{ width: '100%', minWidth: 0 }}>
        <Paragraph
          ellipsis={{ rows: 2, tooltip: summary }}
          style={{ marginBottom: 4, marginTop: 0, fontSize: 12, lineHeight: 1.5 }}
        >
          {summary}
        </Paragraph>
        <Popover
          title={`权限详情（${groups.length} 个模块）`}
          content={fullInPopover}
          trigger="click"
          placement="topLeft"
        >
          <Button type="link" size="small" style={{ padding: 0, height: 'auto' }}>
            查看权限明细
          </Button>
        </Popover>
      </div>
    )
  }

  renderDataScopeCell = (dataScope, dataScopeLabelByValue) => {
    if (dataScope == null || dataScope === '') {
      return <Text type="secondary">—</Text>
    }
    if (typeof dataScope === 'object') {
      const rawValue = dataScope.value != null && dataScope.value !== '' ? dataScope.value : dataScope.id
      const label =
        dataScope.desc || dataScope.label || dataScope.name || dataScopeLabelByValue.get(String(rawValue))
      return renderTextStr(label || rawValue)
    }
    const text = dataScopeLabelByValue.get(String(dataScope)) || dataScope
    return renderTextStr(text)
  }

  /** 角色关联用户（接口 include users）：带门店；单元格约 2 行，超出可点 ··· 按门店查看全部 */
  renderRoleUsersCell = (row, storeNameById, currentUserStoreId) => {
    const users = row.users
    if (!Array.isArray(users) || users.length === 0) {
      return <Text type="secondary">—</Text>
    }
    return (
      <RoleUsersTagsCell
        users={users}
        storeNameById={storeNameById}
        currentUserStoreId={currentUserStoreId}
      />
    )
  }

  getColumns = (storeNameById, currentUserStoreId, dataScopeLabelByValue) => [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 72,
      fixed: 'left'
    },
    {
      title: '角色名称',
      dataIndex: 'name',
      width: 100,
      ellipsis: true,
      render: (v) => {
        const text = renderTextStr(v)
        return (
          <Text
            strong
            style={{ color: PAGE_TOKEN.colorPrimary }}
            ellipsis={{ tooltip: text }}
          >
            {text}
          </Text>
        )
      }
    },
    {
      title: '角色编码',
      dataIndex: 'code',
      width: 140,
      ellipsis: true,
      render: (v) => renderTextStr(v)
    },
    {
      title: '资源范围',
      dataIndex: 'dataScope',
      width: 120,
      ellipsis: true,
      render: (v) => this.renderDataScopeCell(v, dataScopeLabelByValue)
    },
    {
      title: '权限',
      dataIndex: 'permissions',
      ellipsis: false,
      width: 320,
      render: (_, row) => this.renderPermissionCell(row)
    },
    {
      title: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          权限所有用户
          <Tooltip title="蓝色标签表示当前登录账号所属门店。单元格内已优先展示本店人员；点「···」可按门店浏览，本店分组在最上。">
            <QuestionCircleOutlined style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 13 }} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'users',
      ellipsis: false,
      width: 500,
      render: (_, row) => this.renderRoleUsersCell(row, storeNameById, currentUserStoreId)
    },
    {
      title: '备注',
      dataIndex: 'remark',
      width: 260,
      ellipsis: true,
      render: (v) => renderTextStr(v)
    },
    {
      title: '操作',
      key: 'actions',
      width: 300,
      fixed: 'right',
      render: (_, record) => (
        <Space size={0} wrap>
          <Permission rootPath="systemControl">
            <span pathitem={['roleList', 'addMember']} checkarray={['buttonCodes']}>
              <RoleAddMemberModal
                record={record}
                storeNameById={storeNameById}
                onSuccess={this.handleRoleCreateSuccess}
              >
                <Button type="link" size="small" style={{ color: '#1677ff' }}>
                  成员管理
                </Button>
              </RoleAddMemberModal>
            </span>
          </Permission>
          <Permission rootPath="systemControl">
            <span pathitem={['roleList', 'assignPermission']} checkarray={['buttonCodes']}>
              <RoleAssignPermissionDrawer
                record={record}
                onSuccess={this.handleRoleCreateSuccess}
              >
                <Button type="link" size="small" style={{ color: '#1677ff' }}>
                  分配权限
                </Button>
              </RoleAssignPermissionDrawer>
            </span>
          </Permission>
          <Permission rootPath="systemControl">
            <span pathitem={['roleList', 'edit']} checkarray={['buttonCodes']}>
              <RoleCreateDrawer
                actType="edit"
                record={record}
                onSuccess={this.handleRoleCreateSuccess}
              >
                <Button type="link" size="small" style={{ color: '#1677ff' }}>
                  修改
                </Button>
              </RoleCreateDrawer>
            </span>
          </Permission>
          <Permission rootPath="systemControl">
            <span pathitem={['roleList', 'delete']} checkarray={['buttonCodes']}>
              <Popconfirm
                title="确定删除该角色？"
                okText="删除"
                okButtonProps={{ danger: true }}
                onConfirm={() => this.handleDeleteRole(record)}
              >
                <Button type="link" size="small" danger>
                  删除
                </Button>
              </Popconfirm>
            </span>
          </Permission>
        </Space>
      )
    }
  ]

  render() {
    const { fetching, list, jStoreListObj, enumOptions, user } = this.props
    const storeNameById = buildStoreIdToNameMap(jStoreListObj.data)
    const {
      DataScopeType: { data: dataScopeOptions = [] } = {}
    } = enumOptions
    const dataScopeLabelByValue = buildOptionValueToLabelMap(dataScopeOptions)
    const currentUserStoreId = getLoginUserStoreId(user)
    const headerAction = (
      <Permission rootPath="systemControl">
        <span pathitem={['roleList', 'create']} checkarray={['buttonCodes']}>
          <RoleCreateDrawer actType="create" onSuccess={this.handleRoleCreateSuccess}>
            <Button type="primary" icon={<PlusOutlined />}>
              新建角色
            </Button>
          </RoleCreateDrawer>
        </span>
      </Permission>
    )
    return (
      <div>
        <PageHeader
          breadcrumbList={breadcrumbList}
          extra={headerAction}  
  
        />
        <div className="i2-container">
          <Card className="i2-card-page">
            <Table
              loading={fetching}
              columns={this.getColumns(storeNameById, currentUserStoreId, dataScopeLabelByValue)}
              dataSource={list}
              rowKey={(r) => String(r.id)}
              size="small"
              tableLayout="fixed"
              scroll={{ x: 2200 }}
              pagination={false}
            />
          </Card>
        </div>
      </div>
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(RoleListPage)
